import { roundToPrecision } from "../../../helpers/math";
import { hasValue } from "../../../helpers/nullable";
import { centsToDollarsDisplay } from "../../displayFormatters";
import { StatisticItem } from "../ReportStatistics";
import { DaysToCloseData, ProposalDetailedCloseData, ProposalDetailedTurnaroundData, ProposalReportData, ProposalsSummary, ProposalTurnaroundData } from "./proposalsReportApi";

export const getUniqueSalesPersonNames = (proposalsSummary: ProposalsSummary): string[] => {
  const salesPersonNames = new Set<string>();

  Object.values(proposalsSummary).forEach(proposalList => {
    proposalList.forEach((proposal: ProposalReportData) => {
      salesPersonNames.add(proposal.salesPersonName);
    });
  });

  return Array.from(salesPersonNames);
};

export const getUniqueDivisionNames = (proposalsSummary: ProposalsSummary): string[] => {
  const divisionNames = new Set<string>();

  Object.values(proposalsSummary).forEach(proposalList => {
    proposalList.forEach((proposal: ProposalReportData) => {
      if (proposal.divisions) {
        const divisionsList = proposal.divisions.split(';').map(d => d.trim());
        divisionsList.forEach(division => {
          divisionNames.add(division);
        });
      }
    });
  });

  return Array.from(divisionNames);
};

export const getFilteredProposalsData = (proposalsData: ProposalsSummary, salesPersonIds: string[], projectTypeIds: number[], divisionIds: string[]) => {
  const applyFilters = (list: ProposalReportData[]) =>
    list.filter((proposal) =>
      (salesPersonIds.length === 0 || salesPersonIds.includes(proposal.salesPersonName)) &&
      (projectTypeIds.length === 0 || projectTypeIds.includes(proposal.jobType)) &&
      (divisionIds === undefined || divisionIds.length === 0 || divisionIds.some(id => proposal.divisions.includes(id)))
    );

  return {
    unsent: applyFilters(proposalsData.unsent),
    sent: applyFilters(proposalsData.sent),
    approved: applyFilters(proposalsData.approved),
    lost: applyFilters(proposalsData.lost),
    pending: applyFilters(proposalsData.pending)
  };
}

export const getFilteredDetailedCloseData = (detailedCloseData: ProposalDetailedCloseData[], salesPersonIds: string[], projectTypeIds: number[], divisionIds: string[]) => {
  console.log(detailedCloseData)
  const filtered = detailedCloseData.filter((proposal) =>
    (salesPersonIds.length === 0 || salesPersonIds.includes(proposal.salesPersonName)) &&
    (projectTypeIds.length === 0 || projectTypeIds.includes(proposal.jobType)) &&
    (divisionIds.length === 0 || divisionIds.includes(proposal.divisionName))
  );

  console.log(filtered) 

  return{
    totalDaysToClose: filtered.reduce((acc, { daysToClose }) => acc + daysToClose, 0),
    proposalTotal: filtered.length,
    daysToClose: filtered.reduce((acc, { daysToClose }) => acc + daysToClose, 0) / filtered.length
  }
}

export const getFilteredDetailedTurnaroundData = (detailedTurnaroundData: ProposalDetailedTurnaroundData[], salesPersonIds: string[], projectTypeIds: number[], divisionIds: string[]) => {
  const filtered = detailedTurnaroundData.filter((proposal) =>
    (salesPersonIds.length === 0 || salesPersonIds.includes(proposal.salesPersonName)) &&
    (projectTypeIds.length === 0 || projectTypeIds.includes(proposal.jobType)) &&
    (divisionIds.length === 0 || divisionIds.includes(proposal.divisionName))
  )

  return{
    totalProposalTurnaroundDays: filtered.reduce((acc, { proposalTurnaround }) => acc + proposalTurnaround, 0),
    proposalTotal: filtered.length,
    proposalTurnaround: filtered.reduce((acc, { proposalTurnaround }) => acc + proposalTurnaround, 0) / filtered.length
  }
}

export type ProposalSummaryStats = {
  totalProposalsUnsent: number;
  totalProposalsSent: number;
  totalProposalsApproved: number;
  totalProposalsLost: number;
  totalProposalsPending: number;
  totalPriceUnsent: number;
  totalPriceSent: number;
  totalPriceApproved: number;
  totalPriceLost: number;
  totalPricePending: number;
  closeRateByNumberOf: number;
  closeRateByDollars: number;
}

export const getProposalSummaryStats = (proposal: ProposalsSummary): ProposalSummaryStats => ({
  totalProposalsUnsent: proposal.unsent.length,
  totalProposalsSent: proposal.sent.length,
  totalProposalsApproved: proposal.approved.length,
  totalProposalsLost: proposal.lost.length,
  totalProposalsPending: proposal.pending.length,
  totalPriceUnsent: proposal.unsent.reduce((acc, { price }) => acc + price, 0),
  totalPriceSent: proposal.sent.reduce((acc, { price }) => acc + price, 0),
  totalPriceApproved: proposal.approved.reduce((acc, { price }) => acc + price, 0),
  totalPriceLost: proposal.lost.reduce((acc, { price }) => acc + price, 0),
  totalPricePending: proposal.pending.reduce((acc, { price }) => acc + price, 0),
  closeRateByNumberOf:
    proposal.sent.length > 0
      ? roundToPrecision(
        (proposal.approved.length / proposal.sent.length) * 100,
        2
      )
      : 0,
  closeRateByDollars:
    proposal.sent.length > 0
      ? roundToPrecision(
        (proposal.approved.reduce((acc, { price }) => acc + price, 0) / proposal.sent.reduce((acc, { price }) => acc + price, 0)) * 100,
        2
      )
      : 0,
});

export const getProposalStatistics = (
  proposals: ProposalsSummary, 
  daysToCloseData: DaysToCloseData | null | undefined, 
  proposalTurnaroundDaysData: ProposalTurnaroundData | null | undefined
): StatisticItem[] => {
  const summaryStats = getProposalSummaryStats(proposals);
  console.log(daysToCloseData, proposalTurnaroundDaysData)
  return [
    {
      title: 'Total Drafts',
      value: summaryStats.totalProposalsUnsent,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceUnsent) || '',
    },
    {
      title: 'Total Sent',
      value: summaryStats.totalProposalsSent,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceSent) || '',
    },
    {
      title: 'Total Approved',
      value: summaryStats.totalProposalsApproved,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceApproved) || '',
    },
    {
      title: 'Total Lost',
      value: summaryStats.totalProposalsLost,
      extraFinancialData: centsToDollarsDisplay(summaryStats.totalPriceLost) || '',
    },
    {
      title: 'Close Rate by #',
      value: `${summaryStats.closeRateByNumberOf}%`,
      tag: 'Insight',
      tooltip: `The number of sent proposals compared to the amount of approved proposals.  Total Sent: ${summaryStats.totalProposalsSent} Total Approved: ${summaryStats.totalProposalsApproved}`,
    },
    {
      title: 'Close Rate by $',
      value: `${summaryStats.closeRateByDollars}%`,
      tag: 'Insight',
      tooltip: `The dollar amount of sent proposals compared to the dollar amount of approved proposals.  Total $ Sent: ${centsToDollarsDisplay(summaryStats.totalPriceSent)} Total $ Approved: ${centsToDollarsDisplay(summaryStats.totalPriceApproved)}`,
    },
    {
      title: 'Days to Close',
      value: daysToCloseData && isNaN(daysToCloseData.daysToClose) ? '0 days' : hasValue(daysToCloseData?.daysToClose) ? `${roundToPrecision(daysToCloseData?.daysToClose ?? 0, 2)} days` :  '-',
      tag: 'Insight',
      tooltip: `The average time between Sent Proposals and Approval in days.  Proposal Total: ${daysToCloseData?.proposalTotal} Sum of days to close: ${daysToCloseData?.totalDaysToClose}`,
    },
    {
      title: 'Proposal Turnaround Days',
      value: proposalTurnaroundDaysData && isNaN(proposalTurnaroundDaysData.proposalTurnaround) ? '0 days' : hasValue(proposalTurnaroundDaysData?.proposalTurnaround) ? `${roundToPrecision(proposalTurnaroundDaysData?.proposalTurnaround ?? 0, 2)} days` :  '-',
      tag: 'Insight',
      tooltip: `The average time between Draft and Sent Proposals in days.  Proposal Total: ${proposalTurnaroundDaysData?.proposalTotal} Sum of days to close: ${proposalTurnaroundDaysData?.totalProposalTurnaroundDays}`,
    }
  ]
}

export const addApprovedToSent = (proposals: ProposalsSummary): ProposalsSummary => {
  // Create a set of projectIds from the sent array for quick lookup
  const sentProjectIds = new Set(proposals.sent.map(proposal => proposal.projectId));
  
  // Find approved proposals that aren't in the sent array
  const approvedNotInSent = proposals.approved.filter(proposal => !sentProjectIds.has(proposal.projectId));
  
  // Create a new sent array that includes both existing sent proposals and approved proposals not in sent
  const newSentArray = [...proposals.sent, ...approvedNotInSent];
  
  // Return a new ProposalsSummary with the updated sent array
  return {
    ...proposals,
    sent: newSentArray
  };
}